import React, { useState, useEffect, useRef } from 'react';
import { CreditCard, Calendar, AlertCircle, ExternalLink, Activity } from 'lucide-react';
import { useTranslation } from '../../i18n';
import apiClient from '../../utils/api';

const SubscriptionTab = () => {
  const { t } = useTranslation();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchData();
    }
    
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchData = async () => {
    console.log('SubscriptionTab: Fetching subscription data');
    try {
      const [subscriptionResponse, usageResponse] = await Promise.all([
        apiClient.get('/api/subscription/status'),
        apiClient.get('/api/subscription/usage').catch(() => ({ data: null }))
      ]);
      console.log('SubscriptionTab: Received data');
      
      if (isMounted.current) {
        setSubscription(subscriptionResponse.data);
        setUsage(usageResponse.data);
      }
    } catch (err) {
      if (!isMounted.current) return;
      
      if (err.response?.status === 404) {
        setSubscription({ status: 'none' });
      } else {
        console.error('Subscription fetch error:', err);
        setError(t('subscription.fetchError'));
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleManagePayment = () => {
    if (subscription?.customer_portal_url) {
      window.open(subscription.customer_portal_url, '_blank');
    } else {
      alert(t('subscription.portalError'));
    }
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return null;
    const date = new Date(isoDate);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="config-section">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="spinner" style={{ margin: '0 auto' }}></div>
          <p style={{ marginTop: '1rem', color: '#6b7280' }}>{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="config-section">
        <div style={{ 
          padding: '1rem', 
          background: '#fef2f2', 
          border: '1px solid #fecaca',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <AlertCircle size={20} color="#dc2626" />
          <span style={{ color: '#dc2626' }}>{error}</span>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'trial': return '#3b82f6';
      case 'past_due': return '#f59e0b';
      case 'canceled': return '#6b7280';
      case 'expired': return '#ef4444';
      case 'incomplete': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return t('subscription.statusActive');
      case 'trial': return t('subscription.statusTrial');
      case 'past_due': return t('subscription.statusPastDue');
      case 'canceled': return t('subscription.statusCanceled');
      case 'expired': return t('subscription.statusExpired');
      case 'incomplete': return t('subscription.statusIncomplete');
      default: return status;
    }
  };

  const planAmount = subscription?.plan_type === 'monthly'
    ? (subscription?.currency === 'BRL' ? 'R$ 39,90' : '$9.90')
    : (subscription?.currency === 'BRL' ? 'R$ 299,90' : '$99.00');

  const planPeriod = subscription?.plan_type === 'monthly'
    ? t('pricing.perMonth')
    : t('pricing.perYear');

  // No subscription or canceled state
  if (subscription?.status === 'none' || subscription?.status === 'canceled') {
    const isCanceled = subscription?.status === 'canceled';
    
    return (
      <div className="config-section">
        <h2 className="config-section-title">
          <CreditCard size={24} />
          {t('subscription.title')}
        </h2>

        <div style={{
          textAlign: 'center',
          padding: '3rem 2rem',
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px'
        }}>
          <CreditCard size={48} color="#9ca3af" style={{ margin: '0 auto 1rem' }} />
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            {isCanceled ? t('subscription.canceledTitle') : t('subscription.noSubscription')}
          </h3>
          <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
            {isCanceled ? t('subscription.canceledDesc') : t('subscription.noSubscriptionDesc')}
          </p>
          
          <button
            onClick={() => window.open(subscription.buy_url, '_blank')}
            style={{
              padding: '0.75rem 2rem',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
          >
            {isCanceled ? t('subscription.reactivate') : t('subscription.subscribe')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="config-section">
      <h2 className="config-section-title">
        <CreditCard size={24} />
        {t('subscription.title')}
      </h2>

      {/* Current Plan Card */}
      <div style={{
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '1rem'
        }}>
          <div>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              {t('subscription.currentPlan')}
            </h3>
            <div style={{
              display: 'inline-block',
              padding: '0.25rem 0.75rem',
              borderRadius: '9999px',
              background: `${getStatusColor(subscription?.status)}20`,
              color: getStatusColor(subscription?.status),
              fontSize: '0.875rem',
              fontWeight: '600'
            }}>
              {getStatusLabel(subscription?.status)}
            </div>
          </div>
          {subscription?.plan_type && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#3b82f6' }}>
                {planAmount}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                {planPeriod}
              </div>
            </div>
          )}
        </div>

        {subscription?.status === 'trial' && subscription?.days_remaining !== null && (
          <div style={{
            background: '#dbeafe',
            border: '1px solid #93c5fd',
            borderRadius: '8px',
            padding: '1rem',
            marginTop: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <Calendar size={20} color="#3b82f6" />
            <div>
              <div style={{ fontWeight: '600', color: '#1e40af' }}>
                {t('subscription.trialRemaining', { days: subscription.days_remaining })}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#3b82f6', marginTop: '0.25rem' }}>
                {subscription.renewal_date && `Trial ends: ${formatDate(subscription.renewal_date)}`}
              </div>
            </div>
          </div>
        )}

        {subscription?.status === 'active' && subscription?.renewal_date && (
          <div style={{
            background: '#d1fae5',
            border: '1px solid #6ee7b7',
            borderRadius: '8px',
            padding: '1rem',
            marginTop: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <Calendar size={20} color="#10b981" />
            <div>
              <div style={{ fontWeight: '600', color: '#065f46' }}>
                Next billing date
              </div>
              <div style={{ fontSize: '0.875rem', color: '#10b981', marginTop: '0.25rem' }}>
                {formatDate(subscription.renewal_date)}
              </div>
            </div>
          </div>
        )}

        {subscription?.plan_type && (
          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              {t('subscription.planDetails')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>{t('subscription.planType')}</span>
                <span style={{ fontWeight: '500' }}>
                  {subscription.plan_type === 'monthly' ? t('pricing.monthly') : t('pricing.annual')}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>{t('subscription.currency')}</span>
                <span style={{ fontWeight: '500' }}>{subscription.currency}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>{t('subscription.paymentMethod')}</span>
                <span style={{ fontWeight: '500' }}>Stripe</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Credits Usage Card */}
      {usage && (subscription?.status === 'active' || subscription?.status === 'trial') && (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Activity size={20} color="#3b82f6" />
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600' }}>
                {t('subscription.creditsUsage')}
              </h3>
            </div>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {usage.month}
            </span>
          </div>

          {/* Usage Bar */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                {t('subscription.creditsCount', { 
                  used: usage.total_tokens.toLocaleString(), 
                  limit: usage.monthly_limit.toLocaleString() 
                })}
              </span>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: usage.usage_percentage > 80 ? '#ef4444' : '#3b82f6' }}>
                {usage.usage_percentage}%
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '12px',
              background: '#e5e7eb',
              borderRadius: '9999px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${Math.min(usage.usage_percentage, 100)}%`,
                height: '100%',
                background: usage.usage_percentage > 80 ? '#ef4444' : usage.usage_percentage > 60 ? '#f59e0b' : '#10b981',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>

          {/* Credits Breakdown */}
          <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem' }}>
            <div>
              <span style={{ color: '#6b7280' }}>{t('subscription.inputCredits')}: </span>
              <span style={{ fontWeight: '500' }}>{usage.input_tokens.toLocaleString()}</span>
            </div>
            <div>
              <span style={{ color: '#6b7280' }}>{t('subscription.outputCredits')}: </span>
              <span style={{ fontWeight: '500' }}>{usage.output_tokens.toLocaleString()}</span>
            </div>
          </div>

          {usage.usage_percentage > 80 && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              fontSize: '0.875rem',
              color: '#991b1b'
            }}>
              ⚠️ {usage.usage_percentage >= 100 
                ? t('subscription.creditsLimitReached') 
                : t('subscription.creditsWarning')}
            </div>
          )}
        </div>
      )}

      {/* Manage Payment Button */}
      {subscription?.customer_portal_url && (
        <button
          onClick={handleManagePayment}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
        >
          <ExternalLink size={20} />
          {t('subscription.managePayment')}
        </button>
      )}

      {/* Info Section */}
      <div style={{
        marginTop: '1.5rem',
        padding: '1rem',
        background: '#f9fafb',
        borderRadius: '8px',
        fontSize: '0.875rem',
        color: '#6b7280'
      }}>
        <p style={{ marginBottom: '0.5rem' }}>
          <strong>{t('subscription.infoTitle')}</strong>
        </p>
        <ul style={{ paddingLeft: '1.5rem', margin: 0 }}>
          <li>{t('subscription.info1')}</li>
          <li>{t('subscription.info2')}</li>
          <li>{t('subscription.info3')}</li>
        </ul>
      </div>
    </div>
  );
};

export default SubscriptionTab;
